import React from 'react';
import { corelCommunication } from '../lib/corel/communication';
import { 
  XIcon as X,
  ExternalLinkIcon as ExternalLink,
  ActivityIcon as ClipboardCheck,
} from './icons';
import { ShieldAlert } from 'lucide-react';
import { usePermissions } from '../contexts/PermissionContext';

// Substituições mínimas para reduzir dependência; ícones equivalentes simplificados
const Package = (p: any) => <span {...p}/>;
const Clock = (p: any) => <span {...p}/>;
const CheckCircle2 = (p: any) => <span {...p}/>;
const Truck = (p: any) => <span {...p}/>;
const Printer = (p: any) => <span {...p}/>;
const Box = (p: any) => <span {...p}/>;
const PackageCheck = (p: any) => <span {...p}/>;
const PackageOpen = (p: any) => <span {...p}/>;

interface OrderTrackingProps {
  onClose: () => void;
}

// Roles que têm acesso ao acompanhamento de pedidos
const ALLOWED_ROLES = ['revenda', 'cliente', 'admin'] as const;

type OrderStatus = 
  | 'AGUARDANDO PAGAMENTO'
  | 'LIBERADO'
  | 'ARTE FINAL'
  | 'IMPRESSÃO'
  | 'PRENSAGEM'
  | 'CONTROLE DE QUALIDADE'
  | 'SEPARAÇÃO'
  | 'EXPEDIÇÃO'
  | 'DISPONIVEL PARA COLETA';

interface Order {
  id: string;
  createdAt: string;
  status: OrderStatus;
  description: string;
  total: number;
}

const statusConfig: Record<OrderStatus, { icon: React.ElementType; color: string; bgColor: string }> = {
  'AGUARDANDO PAGAMENTO': { 
    icon: Clock, 
    color: 'text-yellow-500',
    bgColor: 'bg-yellow-500/10'
  },
  'LIBERADO': { 
    icon: CheckCircle2, 
    color: 'text-emerald-500',
    bgColor: 'bg-emerald-500/10'
  },
  'ARTE FINAL': { 
    icon: Package, 
    color: 'text-blue-500',
    bgColor: 'bg-blue-500/10'
  },
  'IMPRESSÃO': { 
    icon: Printer, 
    color: 'text-purple-500',
    bgColor: 'bg-purple-500/10'
  },
  'PRENSAGEM': { 
    icon: Box, 
    color: 'text-orange-500',
    bgColor: 'bg-orange-500/10'
  },
  'CONTROLE DE QUALIDADE': { 
    icon: ClipboardCheck, 
    color: 'text-indigo-500',
    bgColor: 'bg-indigo-500/10'
  },
  'SEPARAÇÃO': { 
    icon: PackageCheck, 
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10'
  },
  'EXPEDIÇÃO': { 
    icon: Truck, 
    color: 'text-cyan-500',
    bgColor: 'bg-cyan-500/10'
  },
  'DISPONIVEL PARA COLETA': { 
    icon: PackageOpen, 
    color: 'text-green-500',
    bgColor: 'bg-green-500/10'
  }
};

const mockOrders: Order[] = [
  {
    id: '001',
    createdAt: '2024-03-15 14:30',
    status: 'IMPRESSÃO',
    description: 'DTF TÊXTIL - 5 camisetas',
    total: 150.00
  },
  {
    id: '002',
    createdAt: '2024-03-15 10:15',
    status: 'AGUARDANDO PAGAMENTO',
    description: 'SUBLIMAÇÃO - 3 canecas',
    total: 75.00
  },
  {
    id: '003',
    createdAt: '2024-03-14 16:45',
    status: 'DISPONIVEL PARA COLETA',
    description: 'ADESIVOS ROLAND - 10 adesivos',
    total: 200.00
  }
];

export function OrderTracking({ onClose }: OrderTrackingProps) {
  const { role } = usePermissions();
  
  // 🛡️ SEGURANÇA: Validação interna de role permitido
  const isAllowed = ALLOWED_ROLES.includes(role as typeof ALLOWED_ROLES[number]);

  const handleOpenExternalTracking = (orderId: string) => {
    try { corelCommunication.sendActionGuarded('abrirRastreamento', { orderId } as any); } catch {}
  };

  if (!isAllowed) {
    return (
      <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-navy-800 rounded-lg w-full max-w-md p-6 text-center">
          <ShieldAlert className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-white mb-2">Acesso Negado</h2>
          <p className="text-gray-400 mb-4">Você não tem permissão para acompanhar pedidos.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-navy-700 text-white rounded hover:bg-navy-600 transition-colors"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-navy-900/80 backdrop-blur-sm flex items-center justify-center">
      <div className="bg-navy-800 rounded-lg w-full max-w-2xl animate-fadeIn">
        <div className="flex justify-between items-center p-4 border-b border-navy-700">
          <h2 className="text-xl font-bold text-white">Meus Pedidos</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors rounded-lg p-2 hover:bg-navy-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {mockOrders.map((order) => {
            const StatusIcon = statusConfig[order.status].icon;
            
            return (
              <div key={order.id} className="bg-navy-700 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-white font-medium">Pedido #{order.id}</h3>
                      <div className={`flex items-center px-2 py-1 rounded-full ${statusConfig[order.status].bgColor}`}>
                        <StatusIcon className={`w-4 h-4 ${statusConfig[order.status].color}`} />
                        <span className={`ml-1.5 text-xs font-medium ${statusConfig[order.status].color}`}>
                          {order.status}
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-400 mt-1">{order.description}</p>
                    <div className="flex items-center space-x-3 mt-2">
                      <p className="text-sm text-white font-medium">
                        R$ {order.total.toFixed(2)}
                      </p>
                      <span className="text-sm text-gray-400">{order.createdAt}</span>
                    </div>
                  </div>
                  
                  <button
                    onClick={() => handleOpenExternalTracking(order.id)}
                    className="ml-4 p-2 text-gray-400 hover:text-white hover:bg-navy-600 rounded-lg transition-colors"
                    title="Acompanhamento detalhado"
                  >
                    <ExternalLink className="w-5 h-5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}